import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useHistory } from 'react-router-dom'
import { useMutation } from 'react-query'

import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Home as HomeIcon,
  Print as PrintIcon,
  TrackChanges as TrackChangesIcon,
} from '@material-ui/icons'

import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { PalletLabel } from 'src/components/PrintLabel'
import { PalletDialog } from 'src/modules/supply_chain/components/PalletDialog'
import {
  BooleanField,
  DateTimeField,
  LinkedPathField,
  RelatedLinkedField,
  ValueField,
} from 'src/components/Table'
import { skuit } from 'src/services/skuit'
import { DetailComponent } from './base'

interface ObjInfo {
  data: any
  isLoading: boolean
  refetch: () => void
}

interface DetailTableProps {
  obj: ObjInfo
  refresh?: () => void
  customFields?: Record<string, string>
}

export const PalletDetailTable = (props: DetailTableProps) => {
  const { obj, refresh } = props
  const [updateOpen, setUpdateOpen] = useState(false)
  const { print, renderPrintComponent } = useAsyncPrint()

  const history = useHistory()

  const { mutate: markLost } = useMutation(skuit.sc.containers.lost, {
    onSuccess: () => {
      toast.success(`Successfully sent pallet to lost`)
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response.data}`)
    },
  })

  const { mutate: deleteContainer } = useMutation(skuit.sc.containers.delete, {
    onSuccess: () => {
      toast.success(`Successfully deleted ${obj.data?.name}`)
      history.push('/inventory/pallets')
    },
    onError: (error: any) => {
      toast.error(`Error: ${error.response.data}`)
    },
  })

  const menuItems = [
    {
      Icon: EditIcon,
      actions: [{ title: 'Edit Pallet', onClick: () => setUpdateOpen(true) }],
    },
    {
      Icon: PrintIcon,
      actions: [
        {
          title: 'Print Pallet',
          onClick: () => print((props) => <PalletLabel {...props} obj={obj.data} />),
        },
      ],
    },
    {
      Icon: TrackChangesIcon,
      actions: [
        {
          title: 'Mark Pallet Lost',
          onClick: () => obj.data?.guid && markLost({ guid: obj.data?.guid }),
        },
      ],
    },
  ]

  if (obj.data?.canRetire || obj.data?.canDelete) {
    menuItems.push({
      Icon: DeleteIcon,
      actions: [
        {
          title: 'Delete Pallet',
          onClick: () => obj.data?.guid && deleteContainer(obj.data?.guid),
        },
      ],
    })
  }

  return (
    <>
      {renderPrintComponent()}
      {updateOpen && (
        <PalletDialog
          open={updateOpen}
          pallet={obj.data}
          update
          handleClose={() => {
            setUpdateOpen(false)
          }}
          onSave={refresh}
        />
      )}
      <DetailComponent
        obj={obj}
        Icon={HomeIcon}
        menuItems={menuItems}
        fields={[
          { id: 'guid', label: 'SquID', renderer: ValueField },
          { id: 'name', label: 'Name', renderer: ValueField },
          { id: 'client', label: 'Client', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'clientID', label: 'Client ID', renderer: ValueField },
          { id: 'objPath', label: 'Path', renderer: LinkedPathField },
          { id: 'retiredOn', label: 'Retired On', renderer: DateTimeField },
          { id: 'retiredFrom.objPath', label: 'Retired From', renderer: LinkedPathField },
          { id: 'isLost', label: 'Is Lost', renderer: BooleanField },
          { id: 'forShipping', label: 'For Shipping', renderer: BooleanField },
          { id: 'batch', label: 'Batch', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'internalNotes', label: 'Internal Notes', renderer: ValueField },
          { id: 'reviewNeeded', label: 'Review Needed', renderer: BooleanField },
          { id: 'reviewReason', label: 'Review Reason', renderer: ValueField },
        ]}
      />
    </>
  )
}
